import { filterEmpty } from '@skyeng/libs/shared/rxjs';
import { fromEvent, Observable, of, race, throwError, timer } from 'rxjs';
import { switchMap, shareReplay, mapTo, switchMapTo, map, take, filter } from 'rxjs/operators';

// НЕ МЕНЯТЬ НА jsdelivr CDN, т.к. он плохо работает
const libBaseUrl = 'https://cdn-skypro-storage.skyeng.ru/libs/pyodide/0.21.3'; // min ver for seaborn

export interface Pyodide {
  globals: {
    get: Function;
  };
  runPython: Function;
  runPythonAsync: (code: string, globals?: Object) => Promise<string>;
  loadPackagesFromImports: (code: string, message?: Function, error?: Function) => Promise<string>;
  loadPackage(name: string): Promise<void>;
  pyimport(name: string): { install: (name: string) => Promise<void> };
}

export interface LoadPyodideConfig {
  indexURL: string;
}

declare global {
  interface Window {
    pyodide: Pyodide | undefined;
    loadPyodide: (config: LoadPyodideConfig) => Promise<Pyodide | undefined>;
  }
}

export function getPyodide(): Observable<Pyodide> {
  loadPyodide();
  return timer(0, 100).pipe(
    map(() => window.pyodide),
    filter(pyodide => !!pyodide?.runPython),
    filterEmpty(),
    take(1),
  );
}

let loadingStarted = false;
function loadPyodide(): void {
  if (loadingStarted || window.pyodide) {
    return;
  }
  loadingStarted = true;
  const indexURL = `${libBaseUrl}/`;
  const pyodideUrl = `${libBaseUrl}/pyodide.js`;

  loadScript('pyodide', pyodideUrl).subscribe();

  timer(0, 100)
    .pipe(
      map(() => window.loadPyodide),
      filterEmpty(),
      take(1),
      switchMap(() => {
        return window.loadPyodide({
          indexURL,
        });
      }),
    )
    .subscribe((pyodide: Pyodide | undefined) => {
      if (pyodide) {
        // eslint-disable-next-line functional/immutable-data
        window.pyodide = pyodide;
      }
    });
}

function loadScript(name: string, url: string): Observable<void> {
  const scriptId = `python-exercise-${name}`;

  // script is already loaded
  if (document.querySelector(`#${scriptId}`)) {
    return of(undefined).pipe(shareReplay({ refCount: false, bufferSize: 1 }));
  }

  const scriptElement = document.createElement('script');
  /* eslint-disable functional/immutable-data */
  scriptElement.src = url;
  scriptElement.async = true;
  scriptElement.id = scriptId;
  /* eslint-enable functional/immutable-data */

  const loaded$ = fromEvent(scriptElement, 'load').pipe(mapTo(undefined));
  const error$ = fromEvent(scriptElement, 'error').pipe(
    switchMapTo(throwError(new Error(`Cannot load '${name}' widgets from '${url}' url`))),
  );

  document.body.appendChild(scriptElement);

  return race(loaded$, error$).pipe(shareReplay({ refCount: false, bufferSize: 1 }));
}
